import { Entity, Filter, FilterLogic } from '../../model/SOL49';
import { FilterSettings } from '../../model/client/SOL49';

export enum FilterButtonDirection {
    NONE,
    FORWARD,
    BACKWARD,
}

/**
 * Defines an overlapping filter button together with its state
 */
export class OverlappingFilterButtonDetail<
    ForwardSettings extends FilterSettings<any>,
    BackwardSettings extends FilterSettings<any>,
> {
    enabled: boolean;
    forwardEntity: Entity;
    backwardEntity: Entity;
    direction?: FilterButtonDirection;
    forwardSettings: ForwardSettings;
    backwardSettings: BackwardSettings;

    constructor(
        forwardEntity?: Entity,
        backwardEntity?: Entity,
        forwardSettings?: ForwardSettings,
        backwardSettings?: BackwardSettings,
    ) {
        this.enabled = true;
        this.direction = FilterButtonDirection.NONE;
        this.forwardEntity = forwardEntity;
        this.backwardEntity = backwardEntity;
        this.forwardSettings = forwardSettings;
        this.backwardSettings = backwardSettings;
    }

    /**
     * Returns a copy of the object
     */
    copy(update?: Partial<OverlappingFilterButtonDetail<ForwardSettings, BackwardSettings>>) {
        return Object.assign(new OverlappingFilterButtonDetail<ForwardSettings, BackwardSettings>(), this, update);
    }

    getSettings() {
        if (this.direction === FilterButtonDirection.FORWARD) {
            return this.forwardSettings;
        } else if (this.direction === FilterButtonDirection.BACKWARD) {
            return this.backwardSettings;
        }
        return undefined;
    }
}

export class OverlappingFilterUtils {
    static getFilteredEntity(
        filterButtonDetail: OverlappingFilterButtonDetail<FilterSettings<any>, FilterSettings<any>>,
        entity: Entity,
    ) {
        switch (filterButtonDetail.direction) {
            case FilterButtonDirection.FORWARD:
                return filterButtonDetail.backwardEntity;
            case FilterButtonDirection.BACKWARD:
                return filterButtonDetail.forwardEntity;
        }
        return null;
    }

    static getFilteringEntity(
        filterButtonDetail: OverlappingFilterButtonDetail<FilterSettings<any>, FilterSettings<any>>,
        entity: Entity,
    ) {
        switch (filterButtonDetail.direction) {
            case FilterButtonDirection.FORWARD:
                return filterButtonDetail.forwardEntity;
            case FilterButtonDirection.BACKWARD:
                return filterButtonDetail.backwardEntity;
        }
        return null;
    }

    /**
     * Returns the filters that apply to a given entity, from the provided list of filter button details.
     * It considers the direction of the filter buttons, as well as their other state properties.
     *
     * @param filterButtonDetails
     * @param entity
     */
    static getOverlappingFilterForEntity = (
        filterButtonDetails: OverlappingFilterButtonDetail<FilterSettings<any>, FilterSettings<any>>[],
        entity: Entity,
    ): Filter[] => {
        const filters = new Array<Filter>();

        filterButtonDetails.forEach(filterButtonDetail => {
            const filteredEntity = OverlappingFilterUtils.getFilteredEntity(filterButtonDetail, entity);
            const filteringEntity = OverlappingFilterUtils.getFilteringEntity(filterButtonDetail, entity);
            if (filteredEntity === entity) {
                const filterSettings =
                    filterButtonDetail.direction === FilterButtonDirection.FORWARD
                        ? filterButtonDetail.forwardSettings
                        : filterButtonDetail.backwardSettings;
                filters.push({
                    enabled: filterButtonDetail.enabled,
                    filteringEntity: filteringEntity,
                    filterLogic: filterSettings?.filterLogic || FilterLogic.UNION,
                    filterConfig: filterSettings.config,
                });
            }
        });

        return filters;
    };
}
